import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMapState } from 'rooks';
import qs from 'qs';
import { getBoardDetail, addBoardItem, updateBoardItem } from '@api/api';
import {
  BOARD_NOTICE_POPUP_CODE,
  BOARD_TITLE_MAX_LENGTH,
  BOARD_CONTENT_MAX_LENGTH,
  BOARD_FILE_SIZE_LIMIT,
  BOARD_CHANNEL_CODE_ALL,
} from '@common/utils/board';
import { BoardEditor, BoardFileArea } from '@container/board/elements';
import SatDatePicker from '@components/common/satUI/SatDatePicker';

// @NOTE: 태블릿/모바일 로직 통일을 위해
// 모바일의 stateSelector 처럼 Reducer Selector 별도 분리
const stateSelector = state => ({ ...state.commonReducer });

// 수정 모드일 경우, 서버에서 가져 온 Post 보관 용
let initialPost = {};
let initialPostFiles = [];

const BoardWrite = ({
  type = 'board',
  boardTitle,
  boardCode,
  postTypes,
  moveTo,
  isEdit,
}) => {
  const isNoticeType = useMemo(() => type === 'notice', [type]);
  const isNoticePopup = useMemo(
    () => boardCode === BOARD_NOTICE_POPUP_CODE,
    [boardCode],
  );

  const { search } = useLocation();
  const { info: toastInfo, error: toastError } = $hooks.useToast();
  const { confirm, alert } = $hooks.useModal();

  const { codeInfo } = useSelector(stateSelector);
  const { boardChannelCodes } = codeInfo;

  const { postId } = qs.parse(search.slice(1));

  const [isLoaded, setIsLoaded] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [postFiles, setPostFiles] = useState([]);
  const [postInfo, { set: setPostInfo, setMultiple: changePost }] = useMapState(
    {
      bbsClssCd: '',
      bbsTitle: '',
      bbsContn: '',
      bbsOrgChnlCdReq: '',
      bbsAuthList: [],
      opnStrtDd: '',
      opnEndDd: '',
    },
  );

  // 열람 대상(채널)에 전체 채널이 체크되었는지 여부
  const isAuthAllChecked =
    postInfo.bbsOrgChnlCdReq === BOARD_CHANNEL_CODE_ALL ||
    postInfo.bbsAuthList.includes(BOARD_CHANNEL_CODE_ALL);

  const fetchPost = async () => {
    const { data: fetchedPost, status } = await getBoardDetail({
      params: { postId },
    });

    if (status === 200) {
      const formattedPost = $utils.formatPost(fetchedPost);
      initialPost = $utils.getChanged(postInfo, formattedPost);
      initialPostFiles = fetchedPost.bbsAtchFileList;
      setPostFiles(fp.cloneDeep(initialPostFiles));
      changePost(fp.cloneDeep(initialPost));
    } else {
      moveTo('list');
      toastError('게시글이 없습니다.');
    }

    setIsLoaded(true);
  };

  const handleChange = ({ target }) => {
    const { name, value } = target;
    setPostInfo(name, value);
  };

  const handleFileChange = files => {
    const uploadFiles = $utils.filterFiles(files, 'upload');
    const postFiles = $utils.filterFiles(files, 'post');

    setUploadFiles(uploadFiles);
    setPostFiles(postFiles);
  };

  const toggleBbsAuthAll = () => {
    if (isAuthAllChecked) setPostInfo('bbsAuthList', []);
    else setPostInfo('bbsAuthList', [BOARD_CHANNEL_CODE_ALL]);
  };

  const toggleBbsAuth = bbsAuth => {
    let newBbsAuth = [...postInfo?.bbsAuthList];
    const channelCodeOptions = boardChannelCodes.map(({ cdVal }) => cdVal);

    // 전체가 체크된 상태인 경우에 다른 항목을 체크하였다면 전체 체크 해제
    if (isAuthAllChecked) {
      newBbsAuth = channelCodeOptions.filter(
        channelCode => ![BOARD_CHANNEL_CODE_ALL, bbsAuth].includes(channelCode),
      );
    }

    // 전체 체크가 되지 않은 상태라면, 해당 항목 체크 후, 전체 체크 확인
    else {
      const isAlreadyExist = newBbsAuth.includes(bbsAuth);

      if (isAlreadyExist) {
        const authReqIndex = newBbsAuth.findIndex(item => item === bbsAuth);
        newBbsAuth.splice(authReqIndex, 1);
      } else {
        newBbsAuth.push(bbsAuth);

        // 체크한 bbsAuth 포함 후, 전체를 제외한 모든 항목이 포함 되었다면 전체로 설정
        const isCheckedAll = channelCodeOptions.every(
          channelCode =>
            BOARD_CHANNEL_CODE_ALL === channelCode ||
            newBbsAuth.includes(channelCode),
        );
        if (isCheckedAll) newBbsAuth = [BOARD_CHANNEL_CODE_ALL];
      }
    }

    setPostInfo('bbsAuthList', newBbsAuth);
  };

  const getPostError = postData => {
    const {
      bbsClssCd,
      bbsTitle,
      bbsContn,
      bbsOrgChnlCdReq,
      bbsAuthList,
      opnStrtDd,
      opnEndDd,
    } = postData;
    const totalFileSize = uploadFiles.reduce(
      (total, { size }) => total + size,
      0,
    );

    const isValidAuthList = !isNoticeType || bbsAuthList.length > 0;
    const isValidDisplayPeriod = !isNoticePopup || (!!opnStrtDd && !!opnEndDd);
    const isValidFileSize = totalFileSize <= BOARD_FILE_SIZE_LIMIT;
    const isValid =
      !!bbsTitle?.trim() &&
      !!bbsContn?.trim() &&
      !!bbsClssCd &&
      (!!bbsOrgChnlCdReq || isValidAuthList) &&
      isValidDisplayPeriod;

    if (!isValid) return '필수입력 항목을 입력하세요.';

    if (isNoticePopup) {
      const isValidOpenDate = $utils.checkValidPeriod(opnStrtDd, opnEndDd);
      if (!isValidOpenDate)
        return '게시 시작 기간이 종료 기간보다 뒤로 설정 될 수는 없습니다.';
    }

    if (!isValidFileSize) {
      return '업로드 가능 용량 50MB를 초과하였습니다.';
    }

    /* @NOTE: Text Editor 도입으로, HTML Tag 확인 주석 처리 */
    // if ($utils.checkHtmlTag(bbsTitle))
    //   return '제목에 HTML Tag가 포함되어 있습니다. 삭제후 진행해주세요.';

    // if ($utils.checkHtmlTag(bbsContn))
    //   return '내용에 HTML Tag가 포함되어 있습니다. 삭제후 진행해주세요.';
  };

  const getPostFormData = data => {
    const formData = new FormData();

    Object.keys(data).forEach(key => {
      if (key === 'bbsAuthList') {
        data?.bbsAuthList?.forEach(authReq =>
          formData.append('bbsOrgChnlCdReq', authReq),
        );
      } else if (key === 'bbsContn') {
        const escapedText = $utils.escapeHtml(data[key]);
        formData.append(key, escapedText);
      } else if (data[key]) formData.append(key, data[key]);
    });
    uploadFiles.forEach(file => formData.append('uploadFiles', file));

    return formData;
  };

  const handleBack = () => {
    const initContentInfo = { bbsTitle: '', bbsContn: '', ...initialPost };
    const isChanged = $utils.checkChanged(initContentInfo, postInfo);

    if (isChanged) {
      confirm({
        message: '저장한 내용이 지워집니다. 목록으로 돌아가시겠습니까?',
        onClose: isOk => {
          if (isOk) moveTo('list');
        },
      });
    } else {
      moveTo('list');
    }
  };

  const handleSubmit = async () => {
    try {
      const portError = getPostError(postInfo);
      const isValid = !portError;
      if (!isValid) throw new Error(portError);

      let response;
      if (isEdit) {
        const isChanged = $utils.checkChanged(initialPost, postInfo);
        const isUploadFiles = uploadFiles.length > 0;
        const isDeletedFiles = initialPostFiles.length > postFiles.length;
        if (!isChanged && !isUploadFiles && !isDeletedFiles)
          throw new Error('변경된 사항이 없습니다.');

        const changedPost = $utils.getChanged(initialPost, postInfo);
        const deleteFiles = initialPostFiles.filter(file => {
          const isDeleted = postFiles.every(
            ({ atchFileSno }) => atchFileSno !== file.atchFileSno,
          );
          return isDeleted;
        });

        const deleteFileSnos = deleteFiles.map(
          ({ atchFileSno }) => atchFileSno,
        );
        const formData = getPostFormData({ ...changedPost, deleteFileSnos });

        response = await updateBoardItem({
          payload: { data: formData, postId },
        });
      } else {
        const formData = getPostFormData(postInfo);
        response = await addBoardItem({ payload: formData });
      }

      const isSuccess = $utils.checkApiSuccess(response);

      if (isSuccess) {
        toastInfo('게시물 등록에 성공하였습니다.');
        moveTo('list');
      } else {
        const errorMessage = $utils.getApiErrorMessage(
          response,
          isEdit
            ? '게시물 수정에 실패하였습니다.'
            : '게시물 등록에 실패하였습니다.',
        );

        throw new Error(errorMessage);
      }
    } catch (error) {
      console.log('handleSubmit() - error', error);
      alert({ message: error.message });
    }
  };

  useEffect(() => {
    if (postTypes.length === 1)
      setPostInfo('bbsClssCd', postTypes[0]?.bbsClssCd);
  }, []);

  useEffect(() => {
    if (isEdit) fetchPost();
    else setIsLoaded(true);
  }, [isEdit]);

  return (
    isLoaded && (
      <div className="content manage-content">
        <div className="con-wrap">
          <div className="app-wrap">
            <div className="app-box">
              <div className="tit-area">
                <h2 className="tit-con">{boardTitle}</h2>
              </div>

              <div className="modal-tbl">
                <div className="form-sec">
                  <h4 className="layer-tit3">
                    <label htmlFor="bbsTitle" className="pilsu">
                      제목
                    </label>
                  </h4>

                  <div className="round-box">
                    <input
                      type="text"
                      name="bbsTitle"
                      className="input-text"
                      placeholder="제목을 입력하세요."
                      maxLength={BOARD_TITLE_MAX_LENGTH}
                      value={postInfo.bbsTitle}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-sec">
                  <h4 className="layer-tit3">
                    <label htmlFor="bbsContn" className="pilsu">
                      내용
                    </label>
                  </h4>

                  <div className="round-box">
                    {/* <textarea
                      name="bbsContn"
                      id="bbsContn"
                      cols={50}
                      rows={5}
                      maxLength={BOARD_CONTENT_MAX_LENGTH}
                      placeholder="내용을 입력해주세요."
                      value={postInfo.bbsContn}
                      onChange={handleChange}
                    /> */}

                    <BoardEditor
                      id="bbsContn"
                      name="bbsContn"
                      placeholder="내용을 입력해주세요."
                      value={postInfo.bbsContn}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {isNoticeType ? (
                  <>
                    <div className="form-sec pdt-15 form-row">
                      <div className="col-wrap">
                        <div className="col">
                          <h4 className="layer-tit3">
                            <label htmlFor="memo" className="pilsu">
                              공지사항 열람 대상
                            </label>
                          </h4>

                          <div className="check-box">
                            {boardChannelCodes.map(({ cmnCdNm, cdVal }) => (
                              <div key={cdVal} className="check-wrap chk-style">
                                <input
                                  type="checkbox"
                                  name={`bbsAuth-${cdVal}`}
                                  id={`bbsAuth-${cdVal}`}
                                  checked={
                                    isAuthAllChecked ||
                                    postInfo?.bbsAuthList?.includes(cdVal)
                                  }
                                  onChange={() => {
                                    if (cdVal === BOARD_CHANNEL_CODE_ALL)
                                      toggleBbsAuthAll();
                                    else toggleBbsAuth(cdVal);
                                  }}
                                />
                                <label htmlFor={`bbsAuth-${cdVal}`}>
                                  {cmnCdNm}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {isNoticePopup && (
                      <div className="form-sec">
                        <h4 className="layer-tit3">
                          <label htmlFor="memo" className="pilsu">
                            게시기간
                          </label>
                        </h4>

                        <div className="data-area">
                          <SatDatePicker
                            viewIcon
                            value={postInfo.opnStrtDd}
                            onChange={v => setPostInfo('opnStrtDd', v)}
                          />

                          <span className="tilde">~</span>

                          <SatDatePicker
                            viewIcon
                            value={postInfo.opnEndDd}
                            onChange={v => setPostInfo('opnEndDd', v)}
                          />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="form-sec form-row">
                    <div className="col-wrap">
                      <div className="col">
                        <h4 className="layer-tit3">
                          <label htmlFor="memo" className="pilsu">
                            채널
                          </label>
                        </h4>
                        <div className="sel-group disabled">
                          <select
                            name="bbsOrgChnlCdReq"
                            onChange={handleChange}
                            value={postInfo.bbsOrgChnlCdReq}
                          >
                            {!postInfo.bbsOrgChnlCdReq && (
                              <option value="">선택</option>
                            )}

                            {boardChannelCodes.map(({ cmnCdNm, cdVal }) => (
                              <option key={cdVal} value={cdVal}>
                                {cmnCdNm}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="col">
                        <h4 className="layer-tit3">
                          <label htmlFor="memo" className="pilsu">
                            자료 분류
                          </label>
                        </h4>
                        <div className="sel-group">
                          <select disabled name="boardTitle">
                            <option value="">{boardTitle}</option>
                          </select>
                        </div>
                      </div>

                      <div className="col">
                        <h4 className="layer-tit3">
                          <label htmlFor="memo" className="pilsu">
                            구분
                          </label>
                        </h4>
                        <div className="sel-group">
                          <select
                            name="bbsClssCd"
                            onChange={handleChange}
                            value={postInfo.bbsClssCd}
                          >
                            {!postInfo?.bbsClssCd && (
                              <option value="">선택</option>
                            )}

                            {postTypes.map(
                              ({ bbsClssNm, bbsClssCd }, index) => (
                                <option key={index} value={bbsClssCd}>
                                  {bbsClssNm}
                                </option>
                              ),
                            )}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <BoardFileArea
                  isEdit
                  isNotice={isNoticeType || isNoticePopup}
                  files={[...postFiles, ...uploadFiles]}
                  onChangeFiles={handleFileChange}
                />
              </div>

              <div className="btn-area btn-center">
                <button className="btn-l btn-reg" onClick={handleSubmit}>
                  {isEdit ? '저장' : '등록'}
                </button>
                <button className="btn-l btn-list" onClick={handleBack}>
                  목록
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
};

export default BoardWrite;
